import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckOutlined, CopyOutlined, DownloadOutlined, ImportOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Form, Input, Popconfirm, Row, Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "../../../../../others/i18n.js";
import { spacing, templateFormDefaultProps } from "../../../../../styles/spacing.js";
import { copyToClipboard, downloadFile } from "../../../../../util/export-import.js";
import { EditModal } from "../../../../layout/edit-modal.js";
/**
 * ExportModal Component
 *
 * A React functional component for displaying a modal dialog to export and import data.
 *
 * @component
 * @param exportableData - Data that can be exported.
 * @param open - Controls whether the modal is open or closed.
 * @param onCancel - Callback function to handle the modal cancellation event.
 * @param onSubmit - Callback function to handle the form submission event.
 * @param restProps - Additional props to be passed to the Ant Design Modal component.
 *
 * @returns The rendered ExportModal component.
 */ const ExportModal = ({ exportableData, open, onCancel, onSubmit, ...restProps })=>{
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [exported, setExported] = useState(false);
    const submitForm = ()=>{
        form.submit();
    };
    useEffect(()=>{
        if (open) {
            setExported(false);
            form.resetFields();
        }
    }, [
        open
    ]);
    return open ? /*#__PURE__*/ _jsx(EditModal, {
        title: t("Import/Export"),
        open: open,
        onCancel: onCancel,
        footer: null,
        ...restProps,
        children: /*#__PURE__*/ _jsxs(Form, {
            form: form,
            onFinish: (values)=>{
                onSubmit?.(values.importData);
            },
            ...templateFormDefaultProps,
            children: [
                /*#__PURE__*/ _jsx(Form.Item, {
                    name: "importData",
                    label: t("Import data"),
                    required: true,
                    rules: [
                        {
                            validator: (_, value, callback)=>{
                                try {
                                    if (!value || value.trim().toString().length === 0) {
                                        throw new Error();
                                    }
                                    JSON.parse(value);
                                    callback();
                                } catch (error) {
                                    callback(t("Please paste a valid and not empty JSON string, here."));
                                }
                            }
                        }
                    ],
                    children: /*#__PURE__*/ _jsx(Input.TextArea, {
                        rows: 15
                    })
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    style: {
                        paddingTop: spacing.layout.content.padding
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Col, {
                            style: {
                                flex: "1"
                            }
                        }),
                        /*#__PURE__*/ _jsx(Col, {
                            children: /*#__PURE__*/ _jsxs(Space, {
                                children: [
                                    /*#__PURE__*/ _jsx(CheckOutlined, {
                                        hidden: !exported,
                                        style: {
                                            color: "green"
                                        }
                                    }),
                                    /*#__PURE__*/ _jsxs(Dropdown.Button, {
                                        type: "primary",
                                        onClick: ()=>{
                                            copyToClipboard(exportableData);
                                            setExported(true);
                                        },
                                        title: t("Exports the current template state to your clipboard."),
                                        menu: {
                                            items: [
                                                {
                                                    key: "download",
                                                    label: /*#__PURE__*/ _jsxs(_Fragment, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(DownloadOutlined, {
                                                                style: {
                                                                    paddingRight: spacing.layout.content.paddingSmall
                                                                }
                                                            }),
                                                            t("Download JSON file")
                                                        ]
                                                    }),
                                                    title: t("Download the current template state as JSON file."),
                                                    onClick: ()=>{
                                                        downloadFile({
                                                            data: exportableData,
                                                            fileName: JSON.parse(exportableData)?.identifier,
                                                            fileType: "application/json",
                                                            fileExtension: "json"
                                                        });
                                                    }
                                                }
                                            ]
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(CopyOutlined, {}),
                                            t("Export to clipboard")
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Popconfirm, {
                                        title: t("Are you sure you want to import given template data?"),
                                        onConfirm: submitForm,
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            icon: /*#__PURE__*/ _jsx(ImportOutlined, {}),
                                            type: "primary",
                                            children: t("Import template")
                                        }, "submit")
                                    })
                                ]
                            })
                        })
                    ]
                })
            ]
        })
    }) : null;
};
export { ExportModal };
